/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import 'pdfjs-dist/build/pdf.worker'; // Ensure Webpack includes worker
import { CircularProgress, IconButton, Stack, Typography, Box } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

import { useFilePreviewDialog } from 'contexts/FilePreviewDialogContext';
import { downloadFileData } from 'store/slices/files';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default function PdfViewer({ file = {} }) {
  const dispatch = useDispatch();

  const canvasRef = useRef();
  const containerRef = useRef();
  const [pdfRef, setPdfRef] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const { setFileBlobUrl } = useFilePreviewDialog();

  const renderTaskRef = useRef(null);

  const renderPage = useCallback(
    (pageNum, pdf = pdfRef) => {
      setLoading(true);
      if (!pdf || !canvasRef.current || !containerRef.current) return;

      pdf.getPage(pageNum).then((page) => {
        const container = containerRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = Math.min(containerWidth / unscaledViewport.width, containerHeight / unscaledViewport.height);

        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: context,
          viewport,
        };

        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }

        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;

        renderTask.promise
          .then(() => setLoading(false))
          .catch((error) => {
            if (error?.name !== 'RenderingCancelledException') {
              console.error('Render error:', error);
            }
            setLoading(false);
          });
      });
    },
    [pdfRef]
  );

  useEffect(() => {
    const handleResize = () => {
      if (pdfRef) {
        renderPage(currentPage, pdfRef);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pdfRef, currentPage, renderPage]);

  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (pdfRef) {
      renderPage(currentPage, pdfRef);
    }
  }, [pdfRef, currentPage, renderPage]);

  useEffect(() => {
    if (!file?.blobUrl) return;
    const loadingTask = pdfjsLib.getDocument(file.blobUrl);
    loadingTask.promise.then(
      (loadedPdf) => {
        setPdfRef(loadedPdf);
        setNumPages(loadedPdf.numPages);
      },
      (error) => {
        console.error('Error loading PDF:', error);
        setLoading(false);
      }
    );
  }, [file?.blobUrl]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  useEffect(() => {
    if (file.oid && !file?.blobUrl) {
      setLoading(true);
      dispatch(downloadFileData({ id: file.oid }))
        .unwrap()
        .then(async (data) => {
          setFileBlobUrl(file.id, data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [file.oid, dispatch]);

  const nextPage = () => {
    if (pdfRef && currentPage < numPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <Box
      ref={containerRef}
      className="scroll-container-file-viewer" // Apply the global CSS class
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        padding: 0,
        overflowY: 'auto',
      }}
    >
      {/* PDF Canvas */}
      <canvas
        ref={canvasRef}
        style={{
          display: loading ? 'none' : 'block',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      />

      {loading && <CircularProgress size={24} />}

      {/* Pagination Controls */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '24px',
          padding: '4px 8px',
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
        }}
      >
        <IconButton
          size="small"
          onClick={prevPage}
          disabled={currentPage === 1}
          sx={{
            color: '#fff',
            opacity: currentPage === 1 ? 0.5 : 1,
            transition: 'opacity 0.3s',
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <Typography
          variant="subtitle2"
          sx={{ color: '#fff', fontWeight: '500' }}
        >
          {currentPage} / {numPages || ' -'}
        </Typography>

        <IconButton
          size="small"
          onClick={nextPage}
          disabled={currentPage === numPages}
          sx={{
            color: '#fff',
            opacity: currentPage === numPages ? 0.5 : 1,
            transition: 'opacity 0.3s',
          }}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
